import type { ISession, ISessionInstantiator } from '@monofe/apiservice';
import { IUserProperties } from '@monofe/apiservice';
import { STORE_NAME } from '../state/constants';

export interface IModuleSession {
    subscribeOnLanguageChange: (callback: (languageId: number) => void) => void;
    subscribeOnTourChange: (callback: (languageId: number) => void) => void;
    subscribeOnSessionChange: (callback: (session: ISession | null) => void) => void;
    session: (name: string) => Promise<ISession>;
    active: () => ISession | undefined;
}

function _subscribeOnLanguageChange(
    instantiator: ISessionInstantiator,
    callback: (languageId: number) => void,
) {
    const subscribe = (session: ISession) => {
        session.state.subscribeChanges(
            IUserProperties.LanguageId,
            (key: string, languageId: number) => {
                callback(languageId);
            },
            STORE_NAME,
        );
    };

    const sessionChanged = (newValue: ISession | null) => {
        callback(newValue?.state.getPropertyWithDefault(IUserProperties.LanguageId, -1) ?? -1);

        const activeSession = instantiator.active();

        if (activeSession) {
            subscribe(activeSession);
        }
    };

    instantiator.subscribeActiveChanged(sessionChanged, STORE_NAME);

    const activeSession = instantiator.active();

    if (activeSession) {
        subscribe(activeSession);
    }
}

function _subscribeOnTourChange(
    instantiator: ISessionInstantiator,
    callback: (languageId: number) => void,
) {
    const subscribe = (session: ISession) => {
        session.state.subscribeChanges(
            IUserProperties.TourId,
            (key: string, tourId: number) => {
                callback(tourId);
            },
            STORE_NAME,
        );
    };

    const sessionChanged = (newValue: ISession | null) => {
        callback(newValue?.state.getProperty(IUserProperties.TourId) ?? -1);

        const activeSession = instantiator.active();

        if (activeSession) {
            subscribe(activeSession);
        }
    };

    instantiator.subscribeActiveChanged(sessionChanged, STORE_NAME);

    const activeSession = instantiator.active();

    if (activeSession) {
        subscribe(activeSession);
    }
}

function _subscribeOnSessionChange(
    instantiator: ISessionInstantiator,
    callback: (session: ISession | null) => void,
) {
    instantiator.subscribeActiveChanged(callback, STORE_NAME);
}

function _session(instantiator: ISessionInstantiator, name: string): Promise<ISession> {
    const session = instantiator.open(name);
    return session;
}

function _active(instantiator: ISessionInstantiator): ISession | undefined {
    const session = instantiator.active();
    return session ?? undefined;
}

function ModuleSession(instantiator: ISessionInstantiator): IModuleSession {
    return {
        subscribeOnLanguageChange: callback => {
            _subscribeOnLanguageChange(instantiator, callback);
        },
        subscribeOnTourChange: callback => {
            _subscribeOnTourChange(instantiator, callback);
        },
        subscribeOnSessionChange: callback => {
            _subscribeOnSessionChange(instantiator, callback);
        },
        session: callback => _session(instantiator, callback),
        active: () => _active(instantiator),
    };
}

export default ModuleSession;
